import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { MessageCircle, Send, Loader2, CheckCircle2 } from "lucide-react";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

export default function SupportThread() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => {
    api.get(`/support/thread/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token]);

  const send = async () => {
    if (body.trim().length < 2) return toast.error("Please write a reply first");
    setSending(true);
    try {
      await api.post(`/support/thread/${token}/reply`, { body: body.trim() });
      setBody("");
      toast.success("Your reply has been sent to our team");
      load();
    } catch (e) { toast.error(errText(e)); }
    finally { setSending(false); }
  };

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="support-thread-notfound">
        <MessageCircle size={40} className="mx-auto text-slate-300" />
        <h1 className="font-display text-2xl font-extrabold text-[#12234A] mt-4">Conversation not found</h1>
        <p className="text-slate-500 mt-2">This support link may have expired or is invalid. Please start a new message from our contact page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="support-thread-page">
      <div className="flex items-center gap-2 mb-1">
        <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-[#F5551E]"><MessageCircle size={18} className="text-white" /></span>
        <h1 className="font-display text-2xl font-extrabold text-[#12234A]">Your support conversation</h1>
      </div>
      <p className="text-slate-500 text-sm mb-6">Pick up right where you left off — reply below and our team will get it.</p>

      {!data ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={`s-${i}`} className="h-20 rounded-xl" />)}</div>
      ) : (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4" data-testid="support-thread-original">
            <p className="text-xs font-semibold text-slate-400">You wrote{data.subject ? ` · Re: ${data.subject}` : ""} · {fmt(data.created_at)}</p>
            <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{data.message}</p>
          </div>

          <div className="space-y-3">
            {data.replies.map((r, i) => (
              <div
                key={`r-${i}`}
                data-testid={`support-reply-${r.direction}-${i}`}
                className={`rounded-2xl border p-4 ${r.direction === "inbound"
                  ? "border-slate-200 bg-slate-50 ml-8"
                  : "border-emerald-100 bg-emerald-50 mr-8"}`}
              >
                <p className={`text-[11px] font-semibold flex items-center gap-1 ${r.direction === "inbound" ? "text-slate-500" : "text-emerald-700"}`}>
                  {r.direction === "inbound" ? null : <CheckCircle2 size={12} />} {r.who} · {fmt(r.at)}
                </p>
                <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{r.body}</p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-5 mt-6" data-testid="support-reply-box">
            <label className="text-sm font-semibold text-[#12234A]">Add a reply</label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type your reply to our support team…" data-testid="support-reply-input" className="rounded-xl mt-2 min-h-[120px] text-sm" maxLength={4000} />
            <div className="flex justify-end mt-3">
              <Button onClick={send} disabled={sending} data-testid="support-reply-send" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send reply
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
